import { useState } from "react";
import type { FormEvent } from "react";
import { brand, contactEmail, footerCols, formUrl } from "@/mocks/home";

type Status =
  | { type: "idle" }
  | { type: "sending" }
  | { type: "success"; msg: string }
  | { type: "error"; msg: string };

export default function Footer() {
  const [status, setStatus] = useState<Status>({ type: "idle" });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    const email = String(formData.get("email") || "").trim();

    const fail = (msg: string) => setStatus({ type: "error", msg });
    const succeed = (msg: string) => {
      setStatus({ type: "success", msg });
      form.reset();
    };

    if (!email) {
      fail("请填写你的邮箱地址");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      fail("邮箱格式似乎不太对，请检查后再试");
      return;
    }
    // honeypot hit: silently behave like success, never submit
    if (honeypot) {
      succeed("已收到订阅请求，重大进展将第一时间送达（如需取消可在邮件内退订）。");
      return;
    }

    setStatus({ type: "sending" });
    try {
      const res = await fetch(formUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([k]) => k !== "website_alt")
            .map(([k, v]) => [k, String(v)])
        ).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = responseText ? JSON.parse(responseText) : {};
      } catch {
        parsed = {};
      }
      const lower = responseText.toLowerCase();
      const serverMsg =
        parsed?.meta?.detail || parsed?.meta?.message || parsed?.message || parsed?.code || "";
      const isSpam = lower.includes("spam");
      const ok = res.ok && !isSpam && (parsed?.code === "OK" || (res.status >= 200 && res.status < 300 && !lower.includes("invalidparameter")));

      if (ok) {
        succeed("订阅成功！重大进展与关键澄清将第一时间送达你的邮箱。");
      } else {
        fail(serverMsg || `提交失败（${res.status}），请稍后再试或改用邮箱直接联系。`);
      }
    } catch {
      fail("网络异常，暂时无法提交。请稍后重试。");
    }
  };

  const renderStatus = () => {
    if (status.type === "success")
      return (
        <p className="mt-3 text-xs leading-5 text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2 flex items-center gap-2">
          <i className="ri-checkbox-circle-fill"></i>
          {status.msg}
        </p>
      );
    if (status.type === "error")
      return (
        <p className="mt-3 text-xs leading-5 text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-center gap-2">
          <i className="ri-error-warning-fill"></i>
          {status.msg}
        </p>
      );
    return null;
  };

  return (
    <footer className="bg-[#f4ecdf] border-t border-[#e0c9ae]">
      {/* subscribe band */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-10">
        <div className="bg-white rounded-2xl border border-[#e5d2ba] p-6 md:p-8 grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
          <div>
            <span className="text-[11px] font-semibold text-[#c2410c] tracking-widest">NEWSLETTER</span>
            <h2 className="font-display text-xl md:text-2xl font-bold text-[#241c15] mt-2">
              订阅「热点速递」，重大进展不漏网
            </h2>
            <p className="text-sm leading-6 text-[#7a6a58] mt-2">
              在事件出现关键澄清、官方更正或重大进展时，我们才发一封摘要，不打扰、可随时退订。
            </p>
          </div>
          <form
            data-readdy-form=""
            onSubmit={handleSubmit}
            className="w-full"
            noValidate
          >
            {/* honeypot anti-spam */}
            <input
              type="text"
              name="website_alt"
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              readOnly
              className="off-screen-field"
            />
            <div className="flex flex-col sm:flex-row gap-2.5">
              <div className="relative flex-1">
                <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-[#9a8772] text-sm"></i>
                <input
                  type="email"
                  name="email"
                  placeholder="name@example.com"
                  autoComplete="email"
                  className="w-full h-12 pl-11 pr-4 text-sm rounded-md border border-[#e0c9ae] bg-[#faf5ee] text-[#241c15] placeholder:text-[#b6a38c] focus:outline-none focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/10 transition"
                />
              </div>
              <button
                type="submit"
                disabled={status.type === "sending"}
                className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-[#c2410c] rounded-md px-6 h-12 whitespace-nowrap hover:bg-[#9a3408] disabled:opacity-60 cursor-pointer transition-colors"
              >
                {status.type === "sending" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                    提交中
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill text-base"></i>
                    立即订阅
                  </>
                )}
              </button>
            </div>
            {renderStatus()}
          </form>
        </div>

        {/* footer columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-10">
          <div className="col-span-2 md:col-span-2">
            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-lg bg-[#c2410c] text-white flex items-center justify-center font-bold text-lg">
                曝
              </span>
              <div className="leading-none">
                <div className="font-display font-bold text-lg text-[#241c15]">爆料网</div>
                <div className="text-[10px] text-[#a35f35] tracking-widest mt-1">把每件事讲透</div>
              </div>
            </div>
            <p className="text-[13px] leading-6 text-[#7a6a58] mt-3 max-w-sm">
              {brand.desc}
            </p>
            <div className="flex items-center gap-2 mt-4">
              <span className="inline-flex items-center gap-1.5 text-xs text-[#7a6a58] bg-white border border-[#e5d2ba] rounded-md px-3 py-1.5">
                <i className="ri-mail-fill text-[#c2410c]"></i>
                {contactEmail}
              </span>
            </div>
          </div>

          {footerCols.map((col) => (
            <nav key={col.title}>
              <h3 className="text-sm font-semibold text-[#241c15]">{col.title}</h3>
              <ul className="mt-3 space-y-2">
                {col.links.map((l, i) => (
                  <li key={i}>
                    <a
                      href={l.id}
                      className="text-[13px] text-[#7a6a58] hover:text-[#c2410c] transition-colors inline-flex items-center gap-1.5"
                    >
                      <i className="ri-arrow-right-s-line text-[#c2410c] text-sm"></i>
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>
      </div>

      <div className="border-t border-[#e0c9ae] bg-[#e9dbc6]">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-[#8a7760]">
            © {new Date().getFullYear()} {brand.name} · 坚持来源标注 · 交叉验证 · 公开更正
          </p>
          <p className="text-xs text-[#8a7760] flex items-center gap-2">
            <i className="ri-shield-check-line text-[#c2410c]"></i>
            信息分层标准：已证实 / 待核实 / 明确存疑
          </p>
        </div>
      </div>
    </footer>
  );
}